import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import {
  deleteLevel,
  deleteQuestion,
  deleteSubject,
  getAssessmentTree,
  getLevelQuestions,
  saveQuestion,
  updateLevel,
} from '../../redux/features/assessmentCmsSlice';
import QuestionEditorDialog from './questionbank/QuestionEditorDialog';
import { LADDER_TRACKS, TRACK_META } from './questionbank/tracks';
import AddPaperDialog from './questionbank/AddPaperDialog';
import AddLevelDialog from './questionbank/AddLevelDialog';
import { CMS_ROUTES, PUBLIC_LANDING_URL, siblingPath } from './cmsNav';

/**
 * Assessment Question Bank — the parent-landing papers, editable.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN EDITOR IS LOOKING AT
 *
 * Five kinds of paper live behind one screen, because they are all "questions a
 * visitor answers on the landing page" even though they behave differently:
 *
 *   FOUNDATION (Class 1-4)   A ladder. The child starts at a level, and passing
 *                            it unlocks the next. Five subjects, split into two
 *                            class groups, each with three or four levels.
 *   SECONDARY (Class 5-10)   Same shape. Ships EMPTY — built here.
 *   HIGHER SECONDARY (+1,+2) Same shape. Ships EMPTY — built here.
 *   KG (LKG/UKG)             Ten picture questions. One level, no ladder, no
 *                            pass mark — a four-year-old answers all ten.
 *   PARENT                   Ten questions the parent answers before any paper.
 *                            Five are scored; five profile the child for sales.
 *
 * The tree on the left is the navigation; the panel on the right is one level's
 * questions.
 *
 * ---------------------------------------------------------------------------
 * THE TREE IS GROUPED BY PAPER TYPE, INCLUDING THE EMPTY ONES
 *
 * "Secondary (Class 5-10)" appears as a heading with an Add button before
 * anybody has created a paper under it. That is the whole reason for the
 * grouping: a flat list of existing subjects would never mention the two new
 * tracks, and nobody would guess they could be built.
 *
 * Building one is three steps, in order: create the paper, add its levels, add
 * their questions.
 *
 * ---------------------------------------------------------------------------
 * WHY DELETE IS DISCOURAGED AND "HIDE" IS OFFERED FIRST
 *
 * Question numbering has to stay 1, 2, 3 with no gaps, because the public
 * funnel identifies an answer by `level order * 100 + question number` when it
 * writes to the lead's report. Deleting question 2 therefore renumbers 3 into
 * 2, and every answer already recorded against the old numbering now lines up
 * against a different question in the historical reports.
 *
 * Nothing errors when that happens — the reports just quietly become wrong. So
 * the row offers a Hide switch, which changes nothing about the numbering, and
 * makes Delete a confirmed action that says what it will do.
 */

/**
 * A category number means different class groups in different tracks — 1 is
 * "Class 1 & 2" under foundation and "Class 5, 6 & 7" under secondary. The API
 * ships the labels with each track, so this reads them from there and only
 * falls back to a generic string for a track it has never heard of.
 */
const categoryLabel = (group, category) => {
  const match = (group?.categories || []).find((c) => c.value === Number(category));
  if (match) return match.label;
  return Number(category) === 0 ? 'All classes' : `Class group ${category}`;
};

// LADDER_TRACKS and TRACK_META come from ./questionbank/tracks — see that
// file for why they are not defined here.

const AssessmentQuestionBank = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { tree, level: levelState } = useSelector((state) => state.assessmentCms);

  /**
   * The tree is grouped by PAPER TYPE, not shown as a flat subject list.
   *
   * That is what makes an empty track discoverable: "Secondary (Class 5-10)"
   * appears as a heading with an Add button even before anybody has created a
   * paper under it. A flat list would simply not mention it, and nobody would
   * guess it was possible.
   */
  const grouped = useMemo(() => {
    const byTrack = new Map();
    (tree.tracks || []).forEach((t) => byTrack.set(t.value, { ...t, subjects: [] }));
    (tree.data || []).forEach((subject) => {
      if (!byTrack.has(subject.track)) {
        byTrack.set(subject.track, {
          value: subject.track,
          label: subject.track,
          categories: [],
          isLadder: true,
          subjects: [],
        });
      }
      byTrack.get(subject.track).subjects.push(subject);
    });
    return [...byTrack.values()];
  }, [tree.data, tree.tracks]);

  const [selectedLevelId, setSelectedLevelId] = useState(null);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [editing, setEditing] = useState(null); // null | {} for new | question
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [levelSettings, setLevelSettings] = useState(null);
  const [addingPaper, setAddingPaper] = useState(null); // null | defaultTrack
  const [addingLevel, setAddingLevel] = useState(null); // null | subject row
  const [confirmRemove, setConfirmRemove] = useState(null); // {kind, row}

  useEffect(() => {
    dispatch(getAssessmentTree());
  }, [dispatch]);

  // Open the first subject and select its first level, so the screen is never
  // an empty panel asking the editor to guess where to start.
  useEffect(() => {
    if (!tree.data.length || selectedLevelId) return;
    const first = tree.data.find((subject) => subject.levels?.length);
    if (!first) return;
    setExpandedSubjects({ [first.id]: true });
    setSelectedLevelId(first.levels[0].id);
  }, [tree.data, selectedLevelId]);

  useEffect(() => {
    if (selectedLevelId) dispatch(getLevelQuestions(selectedLevelId));
  }, [selectedLevelId, dispatch]);

  const refresh = () => {
    dispatch(getAssessmentTree());
    if (selectedLevelId) dispatch(getLevelQuestions(selectedLevelId));
  };

  const { level, subject, questions, totalMarks, remainingSlots, loading } = levelState;
  const track = subject?.track || 'foundation';

  /**
   * A level whose questions do not add up to a round number is not an error,
   * but it is worth surfacing: the pass mark is an absolute number of marks, so
   * a level worth 8 needs 6/8 (75%) to clear where a level worth 10 needs 60%.
   * Two live Arabic levels are in exactly that state.
   */
  const passMarkNote = useMemo(() => {
    if (!level?.passMark || !totalMarks) return null;
    const percent = Math.round((level.passMark / totalMarks) * 100);
    return { percent, unusual: percent !== 60 };
  }, [level, totalMarks]);

  const handleDelete = async () => {
    try {
      const res = await dispatch(deleteQuestion(confirmDelete.id)).unwrap();
      enqueueSnackbar('Question deleted', { variant: 'success' });
      if (res?.data?.note) {
        enqueueSnackbar(res.data.note, { variant: 'warning', autoHideDuration: 10000 });
      }
      setConfirmDelete(null);
      refresh();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not delete the question', { variant: 'error' });
    }
  };

  /**
   * Delete a paper or a level.
   *
   * The server refuses while questions remain and says why, so the failure path
   * here is just surfacing that message — there is no client-side gate to keep
   * in step with it.
   */
  const handleRemove = async () => {
    const { kind, row } = confirmRemove;
    try {
      if (kind === 'paper') {
        await dispatch(deleteSubject(row.id)).unwrap();
        enqueueSnackbar(`"${row.label}" deleted`, { variant: 'success' });
      } else {
        await dispatch(deleteLevel(row.id)).unwrap();
        enqueueSnackbar(`"${row.displayName}" deleted`, { variant: 'success' });
        if (selectedLevelId === row.id) setSelectedLevelId(null);
      }
      setConfirmRemove(null);
      refresh();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not delete it', {
        variant: 'error',
        autoHideDuration: 9000,
      });
    }
  };

  const toggleQuestionVisible = async (question) => {
    // Reuses the question save endpoint rather than adding a toggle route —
    // the whole question round-trips, which keeps one validation path.
    try {
      await dispatch(
        saveQuestion({
          id: question.id,
          levelId: level.id,
          text: question.text,
          type: question.type,
          subItems: question.subItems,
          correctAnswers: question.correctAnswers,
          matchMode: question.matchMode,
          optionsLayout: question.optionsLayout,
          options: question.options,
          marks: question.marks,
          promptMedia: question.promptMedia,
          band: question.band,
          isScored: question.isScored,
          dimension: question.dimension,
          isEnabled: !question.isEnabled,
        })
      ).unwrap();
      enqueueSnackbar(question.isEnabled ? 'Question hidden' : 'Question shown', {
        variant: 'success',
      });
      refresh();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not change the question', { variant: 'error' });
    }
  };

  return (
    <GlobalWrapper
      title="Assessment Question Bank"
      actions={
        <Stack direction="row" spacing={1}>
          {/* The other half of this CMS — the copy and imagery on the page this
              funnel sits inside. */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon icon="mdi:text-box-edit-outline" width={16} />}
            onClick={() => navigate(siblingPath(location.pathname, CMS_ROUTES.landing))}
          >
            Landing page CMS
          </Button>
          <Button
            size="small"
            variant="outlined"
            href={PUBLIC_LANDING_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<Icon icon="mdi:open-in-new" width={16} />}
          >
            View page
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2}>
        {/* ------------------------------- tree ------------------------------- */}
        <Grid item xs={12} md={4} lg={3}>
          <Card sx={{ p: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1, pb: 1 }}
            >
              <Typography variant="subtitle2">Papers</Typography>
              <Button
                size="small"
                startIcon={<Icon icon="mdi:plus" width={16} />}
                onClick={() => setAddingPaper('secondary')}
              >
                Add
              </Button>
            </Stack>
            {tree.loading && <CircularProgress size={20} sx={{ m: 2 }} />}

            <List dense disablePadding>
              {grouped.map((group) => (
                <Box key={group.value} sx={{ mb: 1 }}>
                  {/* The paper type is always shown, even with no papers under
                      it — that is how an employee discovers the Class 5-10 and
                      +1/+2 banks exist and can be built. */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ px: 1, pt: 1, pb: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ flex: 1, fontWeight: 700, color: 'text.secondary' }}
                    >
                      {group.label}
                    </Typography>
                    <Tooltip title={`Add a paper for ${group.label}`}>
                      <IconButton size="small" onClick={() => setAddingPaper(group.value)}>
                        <Icon icon="mdi:plus" width={15} />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {group.subjects.length === 0 && (
                    <Typography
                      variant="caption"
                      color="text.disabled"
                      sx={{ px: 1.5, pb: 0.5, display: 'block' }}
                    >
                      No papers yet.
                    </Typography>
                  )}

                  {group.subjects.map((subjectRow) => {
                    const meta = TRACK_META[subjectRow.track] || TRACK_META.foundation;
                    const isOpen = Boolean(expandedSubjects[subjectRow.id]);
                    const questionTotal = (subjectRow.levels || []).reduce(
                      (n, l) => n + l.questionCount,
                      0
                    );
                    return (
                      <Box key={subjectRow.id}>
                        <ListItemButton
                          onClick={() =>
                            setExpandedSubjects((prev) => ({
                              ...prev,
                              [subjectRow.id]: !prev[subjectRow.id],
                            }))
                          }
                          sx={{ borderRadius: 1 }}
                        >
                          <Icon icon={meta.icon} width={18} style={{ marginRight: 8 }} />
                          <Stack sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {subjectRow.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {subjectRow.levels?.length || 0} level
                              {subjectRow.levels?.length === 1 ? '' : 's'} · {questionTotal}{' '}
                              question{questionTotal === 1 ? '' : 's'}
                            </Typography>
                          </Stack>
                          {!subjectRow.isEnabled && (
                            <Chip size="small" label="Off" sx={{ height: 18, mr: 0.5 }} />
                          )}
                          <Icon icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} />
                        </ListItemButton>

                        <Collapse in={isOpen}>
                          {categorySections(group, subjectRow.levels).map(
                            ({ category, levels }) => (
                              <Box key={category} sx={{ pl: 1 }}>
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ pl: 2, pt: 0.5, display: 'block' }}
                                >
                                  {categoryLabel(group, category)}
                                </Typography>
                                {levels.map((levelRow) => (
                                  <ListItemButton
                                    key={levelRow.id}
                                    selected={selectedLevelId === levelRow.id}
                                    onClick={() => setSelectedLevelId(levelRow.id)}
                                    sx={{ pl: 3, borderRadius: 1 }}
                                  >
                                    <Stack sx={{ flex: 1 }}>
                                      <Typography variant="body2">
                                        {/* A paper with no ladder has one level and no
                                            position worth showing — "1." in front of the
                                            only rung reads as the first of several. */}
                                        {levelRow.levelKey === SINGLE_LEVEL_KEY
                                          ? levelRow.displayName
                                          : `${levelRow.levelOrder}. ${levelRow.displayName}`}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {levelRow.questionCount} question
                                        {levelRow.questionCount === 1 ? '' : 's'} ·{' '}
                                        {levelRow.totalMarks} marks
                                      </Typography>
                                    </Stack>
                                    {!levelRow.isEnabled && (
                                      <Chip size="small" label="Off" sx={{ height: 18 }} />
                                    )}
                                  </ListItemButton>
                                ))}

                                {/* One Add per class section, carrying that class with
                                    it. A single Add at the foot of the paper would ask
                                    the author to pick the class again in the dialog,
                                    having just clicked the one they meant. */}
                                <Button
                                  size="small"
                                  startIcon={<Icon icon="mdi:plus" width={14} />}
                                  sx={{ ml: 2, mb: 0.5 }}
                                  onClick={() =>
                                    setAddingLevel({
                                      subject: subjectRow,
                                      group,
                                      category,
                                    })
                                  }
                                >
                                  {levels.length ? 'Add level' : 'Add questions here'}
                                </Button>
                              </Box>
                            ),
                          )}

                          <Stack direction="row" spacing={0.5} sx={{ pl: 2.5, py: 0.5 }}>
                            <Tooltip title="Delete this paper">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setConfirmRemove({ kind: 'paper', row: subjectRow })
                                }
                              >
                                <Icon icon="mdi:trash-can-outline" width={16} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </List>
          </Card>
        </Grid>

        {/* ----------------------------- questions ---------------------------- */}
        <Grid item xs={12} md={8} lg={9}>
          <Card sx={{ p: 2 }}>
            {loading && <CircularProgress size={24} />}

            {!loading && level && (
              <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ sm: 'center' }}
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography variant="h6">
                      {subject?.label}
                      {level.category
                        ? ` — ${categoryLabel(
                            grouped.find((g) => g.value === subject?.track),
                            level.category
                          )}`
                        : ''}{' '}
                      ·{' '}
                      {level.displayName}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${questions.length} questions`}
                      />
                      <Chip size="small" variant="outlined" label={`${totalMarks} marks`} />
                      {level.passMark !== null && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={passMarkNote?.unusual ? 'warning' : 'default'}
                          label={`Pass at ${level.passMark}${passMarkNote ? ` (${passMarkNote.percent}%)` : ''}`}
                        />
                      )}
                      {level.passMark === null && (
                        <Chip size="small" variant="outlined" label="No pass gate" />
                      )}
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon icon="mdi:cog-outline" width={16} />}
                      onClick={() => setLevelSettings(level)}
                    >
                      Level settings
                    </Button>
                    <Tooltip title="Delete this level">
                      <span>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={questions.length > 0}
                          onClick={() => setConfirmRemove({ kind: 'level', row: level })}
                          startIcon={<Icon icon="mdi:trash-can-outline" width={16} />}
                        >
                          Delete
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        remainingSlots <= 0
                          ? 'This level is full. Question numbering would collide with the next level.'
                          : ''
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Icon icon="mdi:plus" width={16} />}
                          disabled={remainingSlots <= 0}
                          onClick={() => setEditing({})}
                        >
                          Add question
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>

                {passMarkNote?.unusual && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This level is worth {totalMarks} marks and passes at {level.passMark}, which is{' '}
                    {passMarkNote.percent}% — most levels are worth 10 and pass at 6 (60%). Either
                    the questions or the pass mark is probably not what was intended.
                  </Alert>
                )}

                {track === 'parent' && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Scored questions all share one 4-point scale where the FIRST option is the
                    healthiest answer. Profiling questions have no right answer — they tell the
                    sales team about the child.
                  </Alert>
                )}

                <Stack spacing={1}>
                  {questions.map((question) => (
                    <QuestionRow
                      key={question.id}
                      question={question}
                      track={track}
                      onEdit={() => setEditing(question)}
                      onDelete={() => setConfirmDelete(question)}
                      onToggle={() => toggleQuestionVisible(question)}
                    />
                  ))}
                  {questions.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }} align="center">
                      No questions in this level yet.
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {addingPaper !== null && (
        <AddPaperDialog
          open
          tracks={tree.tracks}
          defaultTrack={addingPaper}
          onClose={() => setAddingPaper(null)}
          onCreated={refresh}
        />
      )}

      {addingLevel && (
        <AddLevelDialog
          open
          subject={addingLevel.subject}
          existingLevels={addingLevel.subject.levels}
          trackMeta={addingLevel.group}
          category={addingLevel.category}
          // The rungs come from the server for the same reason the tracks do:
          // it decides which keys and positions are legal, so a form that
          // invented its own could only produce rejected saves.
          levelCatalogue={tree.levels}
          onClose={() => setAddingLevel(null)}
          onCreated={refresh}
        />
      )}

      {/* Deleting a paper or a level. The server refuses while questions
          remain, so this dialog's job is to explain the alternative rather
          than to gate anything. */}
      <Dialog open={Boolean(confirmRemove)} onClose={() => setConfirmRemove(null)}>
        <DialogTitle>
          Delete {confirmRemove?.kind === 'paper' ? 'paper' : 'level'}{' '}
          &ldquo;{confirmRemove?.row?.label || confirmRemove?.row?.displayName}&rdquo;?
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            This only works if it is already empty. A paper or level that still has
            questions cannot be deleted — answers already recorded against it would be
            left pointing at something that no longer exists, and those leads&apos; reports
            would stop making sense.
            <br />
            <br />
            To retire it instead, switch it <strong>off</strong> — it disappears from the
            assessment and every stored answer stays readable.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmRemove(null)}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleRemove}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {editing !== null && (
        <QuestionEditorDialog
          open
          question={editing.id ? editing : null}
          level={{ ...level, totalMarks }}
          subject={subject}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {levelSettings && (
        <LevelSettingsDialog
          level={levelSettings}
          totalMarks={totalMarks}
          questionCount={questions.length}
          onClose={() => setLevelSettings(null)}
          onSaved={refresh}
        />
      )}

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete question {confirmDelete?.questionOrder}?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2" gutterBottom>
              {confirmDelete?.text}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Every question after this one moves up a number. Reports for leads who already took
              this paper identify an answer by its number, so their stored answers will line up
              against the new numbering.
              <br />
              <br />
              To retire a question without that effect, use the <strong>Show</strong> switch
              instead — it hides the question and leaves the numbering alone.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="inherit">
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete anyway
          </Button>
        </DialogActions>
      </Dialog>
    </GlobalWrapper>
  );
};

/**
 * The class sections a paper is built out of, EVERY one of them, empty or not.
 *
 * ---------------------------------------------------------------------------
 * WHY EMPTY SECTIONS ARE LISTED
 *
 * This used to group the levels that existed and show only those. Under
 * Secondary that meant a brand-new paper showed nothing at all, and a paper
 * with a Class 5 level showed only "Class 5" — so the six per-class sections
 * that Class 6 through 10 could have were invisible, and an author could only
 * find them by creating a level and discovering the dropdown.
 *
 * It is the same reasoning that puts an empty TRACK in the tree: a section
 * nobody can see is a section nobody builds. So the track's declared class
 * groups drive the list and the levels are filed into them.
 *
 * Categories the server did not declare are still shown rather than dropped —
 * a level authored under an older grouping must remain reachable, or it becomes
 * invisible and undeletable.
 */
const categorySections = (group, levels = []) => {
  const byCategory = new Map();
  (group?.categories || []).forEach((c) => byCategory.set(Number(c.value), []));

  (levels || []).forEach((level) => {
    const key = Number(level.category);
    if (!byCategory.has(key)) byCategory.set(key, []);
    byCategory.get(key).push(level);
  });

  // A track with no declared groups (kg, parent) has one nameless section.
  if (!byCategory.size) byCategory.set(0, levels || []);

  return [...byCategory.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([category, rows]) => ({ category, levels: rows }));
};

/** The key the authoring API gives the one level of a paper with no ladder. */
const SINGLE_LEVEL_KEY = 'single';

const QuestionRow = ({ question, track, onEdit, onDelete, onToggle }) => (
  <Card
    variant="outlined"
    sx={{ p: 1.5, opacity: question.isEnabled ? 1 : 0.55 }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Chip
        size="small"
        label={question.questionOrder}
        sx={{ height: 22, minWidth: 30, fontWeight: 700 }}
      />

      {question.promptMedia?.image && (
        <Box sx={{ fontSize: 28, lineHeight: 1 }}>
          {question.promptMedia.image.includes('/') ? (
            <Box
              component="img"
              src={question.promptMedia.image}
              alt=""
              sx={{ width: 36, height: 36, objectFit: 'contain' }}
            />
          ) : (
            question.promptMedia.image
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {question.text}
        </Typography>

        {question.subItems?.length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block">
            {question.subItems.join('   ')}
          </Typography>
        )}

        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75 }} alignItems="center">
          {LADDER_TRACKS.includes(track) && (
            <Chip size="small" variant="outlined" label={question.type} sx={{ height: 20 }} />
          )}
          {track === 'kg' && question.band && (
            <Chip size="small" variant="outlined" label={question.band} sx={{ height: 20 }} />
          )}
          {track === 'parent' && (
            <Chip
              size="small"
              variant="outlined"
              color={question.isScored ? 'primary' : 'default'}
              label={question.isScored ? 'Scored' : 'Profiling'}
              sx={{ height: 20 }}
            />
          )}
          {track === 'parent' && question.dimension && (
            <Chip size="small" variant="outlined" label={question.dimension} sx={{ height: 20 }} />
          )}
          {track !== 'parent' && (
            <Chip
              size="small"
              variant="outlined"
              label={`${question.marks} mark${question.marks === 1 ? '' : 's'}`}
              sx={{ height: 20 }}
            />
          )}
          {question.matchMode === 'keywords' && (
            <Chip size="small" color="info" label="keyword graded" sx={{ height: 20 }} />
          )}
          {question.optionsLayout === 'nested' && (
            <Chip size="small" label="a list per blank" sx={{ height: 20 }} />
          )}
        </Stack>

        {/* What the grader accepts — the thing an editor most often wants to
            check without opening the question. */}
        {question.accepted?.length > 0 && track !== 'parent' && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              Answer{question.accepted.length > 1 ? 's' : ''}:
            </Typography>
            {question.accepted.map((entry, index) => (
              <Chip
                key={index}
                size="small"
                variant="outlined"
                color="success"
                sx={{ height: 20 }}
                label={
                  entry.mode === 'keywords'
                    ? entry.requires.map((g) => g[0]).join(' + ')
                    : entry.accepts.join(' / ')
                }
              />
            ))}
          </Stack>
        )}
      </Box>

      <Stack direction="row" alignItems="center">
        <Tooltip title={question.isEnabled ? 'Shown in the assessment' : 'Hidden'}>
          <Switch size="small" checked={question.isEnabled} onChange={onToggle} />
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit}>
            <Icon icon="mdi:pencil-outline" width={18} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete}>
            <Icon icon="mdi:trash-can-outline" width={18} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  </Card>
);

/**
 * Level settings — display name, pass mark, and the "FOR PARENTS" bullets shown
 * on the level-selection card.
 *
 * `levelOrder` is deliberately absent. It identifies the level in every lead
 * report ever written, so it is fixed once the level has questions and the
 * server refuses to change it.
 */
const LevelSettingsDialog = ({ level, totalMarks, questionCount, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [displayName, setDisplayName] = useState(level.displayName || '');
  const [passMark, setPassMark] = useState(level.passMark ?? '');
  const [indicators, setIndicators] = useState(level.indicators || []);
  const [isEnabled, setIsEnabled] = useState(level.isEnabled !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await dispatch(
        updateLevel({
          levelId: level.id,
          displayName,
          passMark: passMark === '' ? null : Number(passMark),
          indicators: indicators.filter((point) => point.trim()),
          isEnabled,
        })
      ).unwrap();
      enqueueSnackbar('Level updated', { variant: 'success' });
      onSaved();
      onClose();
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not update the level', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Level settings</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Name shown to parents"
            size="small"
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />

          <TextField
            label="Pass mark"
            size="small"
            fullWidth
            type="number"
            value={passMark}
            onChange={(e) => setPassMark(e.target.value)}
            helperText={
              passMark === ''
                ? 'Empty means no gate — everyone who starts this level finishes the paper.'
                : `${passMark} out of ${totalMarks} marks available (${
                    totalMarks ? Math.round((Number(passMark) / totalMarks) * 100) : 0
                  }%) unlocks the next level.`
            }
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              What this level means, for parents
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Shown as bullet points on the level card.
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {indicators.map((point, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    value={point}
                    onChange={(e) =>
                      setIndicators(indicators.map((p, i) => (i === index ? e.target.value : p)))
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() => setIndicators(indicators.filter((_, i) => i !== index))}
                  >
                    <Icon icon="mdi:close" width={18} />
                  </IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" onClick={() => setIndicators([...indicators, ''])}>
                  Add a point
                </Button>
              </Box>
            </Stack>
          </Box>

          <Divider />

          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
            <Box>
              <Typography variant="body2">Offer this level</Typography>
              <Typography variant="caption" color="text.secondary">
                Turning it off removes the level from the assessment without touching its{' '}
                {questionCount} question{questionCount === 1 ? '' : 's'}.
              </Typography>
            </Box>
          </Stack>

          <Alert severity="info" sx={{ py: 0.5 }}>
            The level&apos;s position in the ladder cannot be changed once it has questions — lead
            reports identify a level by its position, so renumbering it would re-label every answer
            already recorded against it.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssessmentQuestionBank;
